import {useCallback, useEffect, useRef, useState} from "react";
import {Box, IconButton, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PercentIcon from "@mui/icons-material/Percent";
import {select, zoom, ZoomBehavior, zoomIdentity, ZoomTransform} from "d3";
import {INeighbourhood} from "../../api/Model";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {emptyFloorDeskFilter, FloorDeskFilter, FloorDesks, FloorDeskViewMode} from "./FloorDesks";
import {FloorModel} from "./FloorModel";

export type ImageSize = {
    width: number;
    height: number;
};

const VIEWPORT_PADDING = 32;

function fittedScale(viewport: ImageSize, image: ImageSize) {
    return Math.min(
        Math.max(1, viewport.width - VIEWPORT_PADDING * 2) / image.width,
        Math.max(1, viewport.height - VIEWPORT_PADDING * 2) / image.height,
    );
}

export function FloorPlanViewer({src, alt, floor}: {
    src?: string | null,
    alt: string,
    floor: FloorModel,
}) {
    const viewportRef = useRef<SVGSVGElement>(null);
    const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | undefined>(undefined);
    const imageSizeRef = useRef<ImageSize | undefined>(undefined);
    const transformRef = useRef<ZoomTransform>(zoomIdentity);
    const renderedImageSizeRef = useRef<ImageSize | undefined>(undefined);
    const viewportSizeRef = useRef<ImageSize | undefined>(undefined);
    const [imageSize, setImageSize] = useState<ImageSize>();
    const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);
    const [deskFilter, setDeskFilter] = useState<FloorDeskFilter>(emptyFloorDeskFilter);
    const [deskViewMode, setDeskViewMode] = useState<FloorDeskViewMode>('assignments');

    useEffect(() => {
        setDeskFilter(emptyFloorDeskFilter);
    }, [floor.floor.id]);

    const toggleNeighbourhood = useCallback((neighbourhood: INeighbourhood) => {
        setDeskFilter(prevFilter => {
            const selected = prevFilter.neighbourhoods?.find(hood => hood.key === neighbourhood.key) != null;
            return {
                ...prevFilter, neighbourhoods: selected
                    ? prevFilter.neighbourhoods?.filter(hood => hood.key !== neighbourhood.key)
                    : [...(prevFilter.neighbourhoods ?? []), neighbourhood]
            };
        });
    }, [setDeskFilter]);

    const fitToViewport = useCallback((animate = true) => {
        const viewport = viewportRef.current;
        const size = imageSizeRef.current;
        const zoomBehaviour = zoomRef.current;
        if (!viewport || !size || !zoomBehaviour) {
            return;
        }

        const {width, height} = viewport.getBoundingClientRect();
        if (width <= 0 || height <= 0) {
            return;
        }

        const scale = fittedScale({width, height}, size);
        const fittedTransform = zoomIdentity
            .translate(width / 2, height / 2)
            .scale(scale)
            .translate(-size.width / 2, -size.height / 2);

        zoomBehaviour
            .extent([[0, 0], [width, height]])
            .translateExtent([
                [-size.width * 0.5, -size.height * 0.5],
                [size.width * 1.5, size.height * 1.5],
            ])
            .scaleExtent([scale * 0.5, scale * 8]);

        const selection = select(viewport);
        if (animate) {
            selection.transition().duration(200).call(zoomBehaviour.transform, fittedTransform);
        } else {
            selection.call(zoomBehaviour.transform, fittedTransform);
        }
    }, []);

    useEffect(() => {
        if (!src) {
            setImageSize(undefined);
            imageSizeRef.current = undefined;
            return;
        }

        let active = true;
        const image = new Image();
        image.onload = () => {
            if (!active) {
                return;
            }
            const size = {width: image.naturalWidth, height: image.naturalHeight};
            imageSizeRef.current = size;
            setImageSize(size);
        };
        image.src = src;
        return () => {
            active = false;
        };
    }, [src]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || !imageSize) {
            return;
        }

        const zoomBehaviour = zoom<SVGSVGElement, unknown>()
            .on('zoom', event => {
                transformRef.current = event.transform;
                setTransform(event.transform);
            });
        zoomRef.current = zoomBehaviour;
        select(viewport).call(zoomBehaviour);

        const previousTransform = transformRef.current;
        const previousImageSize = renderedImageSizeRef.current;
        fitToViewport(false);
        if (previousImageSize) {
            const {width, height} = viewport.getBoundingClientRect();
            const viewportSize = {width, height};
            const previousFitScale = fittedScale(viewportSize, previousImageSize);
            const nextFitScale = fittedScale(viewportSize, imageSize);
            const relativeZoom = previousTransform.k / previousFitScale;
            const centerX = Math.min(1, Math.max(0, (width / 2 - previousTransform.x) /
                previousTransform.k / previousImageSize.width));
            const centerY = Math.min(1, Math.max(0, (height / 2 - previousTransform.y) /
                previousTransform.k / previousImageSize.height));
            const preservedTransform = zoomIdentity
                .translate(width / 2, height / 2)
                .scale(nextFitScale * relativeZoom)
                .translate(-centerX * imageSize.width, -centerY * imageSize.height);
            select(viewport).call(zoomBehaviour.transform, preservedTransform);
        }
        renderedImageSizeRef.current = imageSize;

        const resizeObserver = new ResizeObserver(entries => {
            const {width, height} = entries[0].contentRect;
            const previousSize = viewportSizeRef.current;
            viewportSizeRef.current = {width, height};
            if (previousSize && (previousSize.width !== width || previousSize.height !== height)) {
                fitToViewport(false);
            }
        });
        resizeObserver.observe(viewport);
        return () => {
            resizeObserver.disconnect();
            select(viewport).on('.zoom', null);
            zoomRef.current = undefined;
        };
    }, [fitToViewport, imageSize]);

    const zoomBy = useCallback((factor: number) => {
        if (viewportRef.current && zoomRef.current) {
            select(viewportRef.current)
                .transition()
                .duration(150)
                .call(zoomRef.current.scaleBy, factor);
        }
    }, []);

    if (!src) {
        return (
            <Paper variant="outlined" sx={viewportSx}>
                <Stack alignItems="center" justifyContent="center" gap={1} height="100%" color="text.secondary">
                    <MapOutlinedIcon fontSize="large"/>
                    <Typography variant="body2">No floor plan available</Typography>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{...viewportSx, position: 'relative', overflow: 'hidden'}}>
            <Box
                ref={viewportRef}
                component="svg"
                role="img"
                aria-label={alt}
                sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    cursor: 'grab',
                    touchAction: 'none',
                    '&:active': {cursor: 'grabbing'}
                }}
            >
                {imageSize && (
                    <g transform={transform.toString()}>
                        <image href={src} width={imageSize.width} height={imageSize.height}/>
                        <FloorDesks
                            floorModel={floor}
                            imageSize={imageSize}
                            filter={deskFilter}
                            viewMode={deskViewMode}
                        />
                    </g>
                )}
            </Box>

            {floor.floorNeighbourhoods.length > 0 && (
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    sx={{
                        position: 'absolute',
                        top: {xs: 64, sm: 12},
                        left: 12,
                        maxWidth: {xs: 'calc(100% - 24px)', sm: 'calc(100% - 300px)'},
                    }}
                >
                    {floor.floorNeighbourhoods.map(neighbourhood => (
                        <ToggleButton
                            key={neighbourhood.id}
                            value={neighbourhood.id}
                            selected={deskFilter.neighbourhoods?.find(hood => hood.key === neighbourhood.key) != null}
                            onChange={() => toggleNeighbourhood(neighbourhood)}
                            size="small"
                            sx={{
                                gap: 0.75,
                                bgcolor: 'background.paper',
                                boxShadow: 2,
                                textTransform: 'none',
                                '&:hover': {bgcolor: 'background.paper'},
                            }}
                        >
                            <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                            {neighbourhood.name}
                        </ToggleButton>
                    ))}
                </Stack>
            )}

            <Stack
                direction="row"
                gap={1}
                sx={{position: 'absolute', top: 12, right: 12}}
            >
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={deskViewMode}
                    onChange={(_, mode: FloorDeskViewMode | null) => mode && setDeskViewMode(mode)}
                    aria-label="Desk view mode"
                    sx={{bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2}}
                >
                    <ToggleButton value="assignments" aria-label="Show desk assignments">
                        <Tooltip title="Show desk assignments">
                            <PersonOutlineIcon/>
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="usage" aria-label="Show desk usage">
                        <Tooltip title="Show desk usage">
                            <PercentIcon/>
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>

                <Stack direction="row" sx={{bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2}}>
                    <Tooltip title="Zoom out"><span>
                        <IconButton aria-label="Zoom out" onClick={() => zoomBy(0.8)} disabled={!imageSize}>
                            <ZoomOutIcon/>
                        </IconButton>
                    </span></Tooltip>
                    <Tooltip title="Zoom in"><span>
                        <IconButton aria-label="Zoom in" onClick={() => zoomBy(1.25)} disabled={!imageSize}>
                            <ZoomInIcon/>
                        </IconButton>
                    </span></Tooltip>
                    <Tooltip title="Fit floor plan to view"><span>
                        <IconButton aria-label="Fit floor plan to view" onClick={() => fitToViewport()}
                                    disabled={!imageSize}>
                            <FitScreenIcon/>
                        </IconButton>
                    </span></Tooltip>
                </Stack>
            </Stack>
        </Paper>
    );
}

const viewportSx = {
    width: '100%',
    height: {xs: 'calc(100dvh - 150px)', md: 'calc(100dvh - 180px)'},
    minHeight: {xs: 360, md: 520},
    bgcolor: 'action.hover',
};
